'use client'

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import {
  getWeeklyJobBoard,
  WeeklyJobBoardResponse,
} from '../../api/work-orders.api'
import { WorkOrderApprovalModal } from './WorkOrderApprovalModal'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import {
  CalendarCheck,
  MapPin,
  Phone,
  Users,
  Star,
  CheckCircle2,
  Clock,
  Camera,
  ExternalLink,
  Navigation,
  CheckSquare,
  Square,
  AlertCircle,
  Loader2,
  RefreshCw,
} from 'lucide-react'
import { buildGoogleMapsPlaceUrl } from '@/shared/lib/utils/google-maps-url'
import { buildRegionAwareHref, cn } from '@/shared/lib/utils'
import { DASHBOARD_ROUTE } from '@/shared/constants/pages-routes'
import { useRegion } from '@/shared/hooks/use-region'
import Link from 'next/link'
import { toast } from 'sonner'

function getTodayISO(): string {
  const d = new Date()
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function TodayWorkOrdersView() {
  const { regionId, region } = useRegionId()
  const { regionSlug } = useRegion()
  const { session } = useSession()

  const [boardData, setBoardData] = useState<WeeklyJobBoardResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedApprovalWo, setSelectedApprovalWo] = useState<any | null>(null)
  const [attendance, setAttendance] = useState<Record<string, boolean>>({})
  const [filterAllJobs, setFilterAllJobs] = useState(false)

  const todayISO = useMemo(() => getTodayISO(), [])
  const todayFormatted = useMemo(() => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }, [])

  // Load attendance check-offs from local storage for today
  useEffect(() => {
    try {
      const saved = localStorage.getItem(`ar_attendance_${todayISO}`)
      if (saved) {
        setAttendance(JSON.parse(saved))
      }
    } catch {
      // ignore
    }
  }, [todayISO])

  const toggleAttendance = (key: string) => {
    const next = { ...attendance, [key]: !attendance[key] }
    setAttendance(next)
    try {
      localStorage.setItem(`ar_attendance_${todayISO}`, JSON.stringify(next))
    } catch {
      // ignore
    }
  }

  // Calculate current week Monday & Saturday
  const { weekStartStr, weekEndStr } = useMemo(() => {
    const d = new Date()
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)
    const mon = new Date(d.setDate(diff))
    const sat = new Date(mon)
    sat.setDate(sat.getDate() + 5)
    return {
      weekStartStr: mon.toISOString().split('T')[0],
      weekEndStr: sat.toISOString().split('T')[0],
    }
  }, [])

  const loadTodayData = useCallback(async () => {
    if (!regionId) return
    setIsLoading(true)
    try {
      const res = await getWeeklyJobBoard(regionId, weekStartStr, weekEndStr)
      if (res.success && res.data) {
        setBoardData(res.data)
      } else {
        toast.error((res as any).message || 'Failed to load today assignments')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error loading today data')
    } finally {
      setIsLoading(false)
    }
  }, [regionId, weekStartStr, weekEndStr])

  useEffect(() => {
    loadTodayData()
  }, [loadTodayData])

  const currentUserId = session?.sub
  const isManager =
    session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER

  // Filter work orders active TODAY
  const allTodayWorkOrders = useMemo(() => {
    if (!boardData?.workOrders) return []
    return boardData.workOrders.filter((wo) => {
      if (Array.isArray(wo.workOrderHours) && wo.workOrderHours.length > 0) {
        return wo.workOrderHours.some((h: any) => h.date === todayISO)
      }
      return wo.dateDelegated === todayISO
    })
  }, [boardData, todayISO])

  // Filter work orders assigned to the logged-in user TODAY
  const myTodayWorkOrders = useMemo(() => {
    return allTodayWorkOrders.filter((wo) => {
      const isLead = wo.teamLead?.id === currentUserId || wo.teamLeadId === currentUserId

      let isVolunteerOnDate = false
      if (Array.isArray(wo.workOrderHours)) {
        const todayEntry = wo.workOrderHours.find((h: any) => h.date === todayISO)
        if (todayEntry && Array.isArray(todayEntry.volunteerIds)) {
          isVolunteerOnDate = todayEntry.volunteerIds.includes(currentUserId)
        }
      }
      if (!isVolunteerOnDate && Array.isArray(wo.volunteers)) {
        isVolunteerOnDate = wo.volunteers.some((v: any) => v.id === currentUserId)
      }

      return isLead || isVolunteerOnDate
    })
  }, [allTodayWorkOrders, currentUserId, todayISO])

  const displayOrders =
    isManager && filterAllJobs ? allTodayWorkOrders : myTodayWorkOrders

  return (
    <div className="max-w-4xl mx-auto space-y-4 pb-12">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background p-4 rounded-xl border shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <CalendarCheck className="w-5 h-5 text-primary" />
            <h2 className="text-base font-bold text-foreground">Today&apos;s Work & Crew</h2>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{todayFormatted}</p>
        </div>

        <div className="flex items-center gap-2">
          {isManager && (
            <Button
              size="sm"
              variant={filterAllJobs ? 'default' : 'outline'}
              onClick={() => setFilterAllJobs(!filterAllJobs)}
              className="h-8 text-xs"
            >
              {filterAllJobs ? "Showing All Base Work" : "Show All Base Work"}
            </Button>
          )}

          <Button
            size="sm"
            variant="ghost"
            onClick={loadTodayData}
            className="h-8 text-xs text-muted-foreground hover:text-foreground gap-1"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isLoading && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Main Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          Loading today&apos;s assignments...
        </div>
      ) : displayOrders.length === 0 ? (
        <div className="p-8 border rounded-xl bg-card text-center space-y-3 shadow-sm">
          <div className="w-12 h-12 rounded-full bg-primary/10 text-primary mx-auto flex items-center justify-center">
            <CalendarCheck className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">No work orders assigned for you today</h3>
            <p className="text-xs text-muted-foreground max-w-sm mx-auto mt-1">
              You are currently not assigned to an active work order on {todayFormatted}. Check with
              your Base Leader or review the full Job Board.
            </p>
          </div>
          {isManager && !filterAllJobs && allTodayWorkOrders.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFilterAllJobs(true)}
              className="text-xs"
            >
              View {allTodayWorkOrders.length} active jobs at the base today
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {displayOrders.map((wo) => {
            const homeowner = wo.home?.homeowner
            const homeownerName =
              homeowner?.user?.fullname || homeowner?.name || 'Homeowner'
            const phone = homeowner?.phone || homeowner?.user?.phone
            const address = wo.home?.address || wo.workAddress
            const workScopeName =
              wo.workScope?.workScopeDisplay || wo.workOrderType || 'Work Order'

            const isTeamLead =
              wo.teamLead?.id === currentUserId || wo.teamLeadId === currentUserId
            const approval = wo.approvalStatus || 'SCHEDULED'
            const isCompleted = approval === 'COMPLETED'
            const isPendingReview = approval === 'PENDING_REVIEW'

            // Extract today's assigned crew
            const todayEntry = Array.isArray(wo.workOrderHours)
              ? wo.workOrderHours.find((h: any) => h.date === todayISO)
              : null
            const dailyVolunteerIds = todayEntry?.volunteerIds || []

            // Crew members for today
            const assignedVolunteers = Array.isArray(wo.volunteers)
              ? wo.volunteers.filter((v: any) =>
                  dailyVolunteerIds.length > 0
                    ? dailyVolunteerIds.includes(v.id)
                    : true
                )
              : []

            const totalCrewCount = (wo.teamLead ? 1 : 0) + assignedVolunteers.length

            return (
              <div
                key={wo.id}
                className={cn(
                  'p-4 rounded-xl border bg-card shadow-sm space-y-3.5 transition-all',
                  isTeamLead && 'border-primary/40 bg-primary/[0.02]'
                )}
              >
                {/* Top: Scope, Role Badge, and Status */}
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-primary text-white text-xs px-2.5 py-0.5 font-bold">
                        {workScopeName}
                      </Badge>

                      {isTeamLead ? (
                        <Badge
                          variant="secondary"
                          className="bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300 text-xs gap-1 border border-amber-300 font-semibold"
                        >
                          <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                          You are Team Lead
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-300 border-blue-300 text-xs"
                        >
                          Volunteer Crew
                        </Badge>
                      )}
                    </div>

                    <h3 className="text-base font-bold text-foreground">{homeownerName}</h3>
                  </div>

                  {/* Status Pill */}
                  <div>
                    {isCompleted ? (
                      <Badge className="bg-emerald-600 text-white text-xs gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Completed
                      </Badge>
                    ) : isPendingReview ? (
                      <Badge
                        variant="secondary"
                        className="bg-amber-100 text-amber-900 border border-amber-300 text-xs gap-1"
                      >
                        <Clock className="w-3.5 h-3.5" /> Pending Review
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        In Progress
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Location & Quick Actions */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-2 border-t text-xs">
                  <div className="space-y-1">
                    {address && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span>{address}</span>
                      </div>
                    )}
                    {phone && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Phone className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span>{phone}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Navigation Buttons */}
                  <div className="flex items-center gap-2 pt-1 sm:pt-0">
                    {address && (
                      <Button
                        size="sm"
                        variant="outline"
                        asChild
                        className="h-8 text-xs gap-1.5"
                      >
                        <a
                          href={buildGoogleMapsPlaceUrl(address)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Navigation className="w-3.5 h-3.5 text-blue-600" />
                          Directions
                        </a>
                      </Button>
                    )}

                    {phone && (
                      <Button
                        size="sm"
                        variant="outline"
                        asChild
                        className="h-8 text-xs gap-1.5"
                      >
                        <a href={`tel:${phone.replace(/[^\d+]/g, '')}`}>
                          <Phone className="w-3.5 h-3.5 text-emerald-600" />
                          Call
                        </a>
                      </Button>
                    )}
                  </div>
                </div>

                {/* Accountability Crew List ("Who is working today") */}
                <div className="p-3 bg-muted/40 rounded-xl border space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-xs flex items-center gap-1.5 text-foreground">
                      <Users className="w-4 h-4 text-primary" />
                      <span>Today&apos;s Crew ({totalCrewCount} on site)</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      Click checkmark for on-site roll call
                    </span>
                  </div>

                  <div className="space-y-1.5">
                    {/* Team Leader Entry */}
                    {wo.teamLead && (
                      <div
                        onClick={() => toggleAttendance(`${wo.id}-lead-${wo.teamLead.id}`)}
                        className={cn(
                          'p-2 rounded-lg border text-xs flex items-center justify-between gap-2 bg-background cursor-pointer hover:border-amber-400 transition-colors shadow-sm',
                          attendance[`${wo.id}-lead-${wo.teamLead.id}`] &&
                            'border-emerald-400 bg-emerald-50/20'
                        )}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {attendance[`${wo.id}-lead-${wo.teamLead.id}`] ? (
                            <CheckSquare className="w-4 h-4 text-emerald-600 shrink-0" />
                          ) : (
                            <Square className="w-4 h-4 text-muted-foreground shrink-0" />
                          )}
                          <div className="truncate">
                            <div className="font-semibold text-foreground flex items-center gap-1.5 truncate">
                              <span>{wo.teamLead.fullname}</span>
                              <Badge
                                variant="secondary"
                                className="bg-amber-100 text-amber-800 text-[10px] px-1 py-0 h-4"
                              >
                                Team Lead
                              </Badge>
                            </div>
                            {wo.teamLead.email && (
                              <div className="text-[10px] text-muted-foreground truncate">
                                {wo.teamLead.email}
                              </div>
                            )}
                          </div>
                        </div>
                        {attendance[`${wo.id}-lead-${wo.teamLead.id}`] && (
                          <span className="text-[10px] text-emerald-700 font-semibold shrink-0">
                            Present
                          </span>
                        )}
                      </div>
                    )}

                    {/* Assigned Volunteers on this Date */}
                    {assignedVolunteers.length === 0 && !wo.teamLead ? (
                      <div className="text-[11px] text-muted-foreground py-2 text-center">
                        No volunteers staffed yet for today.
                      </div>
                    ) : (
                      assignedVolunteers.map((vol: any) => {
                        const attKey = `${wo.id}-vol-${vol.id}`
                        const isPresent = !!attendance[attKey]

                        return (
                          <div
                            key={vol.id}
                            onClick={() => toggleAttendance(attKey)}
                            className={cn(
                              'p-2 rounded-lg border text-xs flex items-center justify-between gap-2 bg-background cursor-pointer hover:border-primary/40 transition-colors shadow-sm',
                              isPresent && 'border-emerald-400 bg-emerald-50/20'
                            )}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {isPresent ? (
                                <CheckSquare className="w-4 h-4 text-emerald-600 shrink-0" />
                              ) : (
                                <Square className="w-4 h-4 text-muted-foreground shrink-0" />
                              )}
                              <div className="truncate">
                                <div className="font-medium text-foreground truncate">
                                  {vol.fullname || vol.name}
                                </div>
                                <div className="text-[10px] text-muted-foreground truncate">
                                  {vol.email || vol.role?.name || 'Volunteer Crew'}
                                </div>
                              </div>
                            </div>
                            {isPresent && (
                              <span className="text-[10px] text-emerald-700 font-semibold shrink-0">
                                Present
                              </span>
                            )}
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>

                {/* Bottom Actions for Team Leader / Volunteer */}
                <div className="pt-2 border-t flex items-center justify-between gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="ghost"
                    asChild
                    className="h-8 text-xs text-muted-foreground hover:text-foreground gap-1"
                  >
                    <Link
                      href={
                        regionSlug
                          ? buildRegionAwareHref(
                              regionSlug,
                              `${DASHBOARD_ROUTE.workOrders}/${wo.id}`
                            )
                          : `/work-orders/${wo.id}`
                      }
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      View Details
                    </Link>
                  </Button>

                  {/* Team Leader Completion Action */}
                  {(isTeamLead || isManager) && (
                    <Button
                      size="sm"
                      onClick={() => setSelectedApprovalWo(wo)}
                      className="h-8 text-xs font-bold gap-1.5 bg-primary hover:bg-primary/90 text-white"
                    >
                      <Camera className="w-3.5 h-3.5" />
                      {isCompleted
                        ? 'View Completion Photos'
                        : 'Submit Photos & Completion'}
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Completion & Review Modal */}
      {selectedApprovalWo && (
        <WorkOrderApprovalModal
          isOpen={true}
          onClose={() => setSelectedApprovalWo(null)}
          workOrder={selectedApprovalWo}
          onSaved={loadTodayData}
        />
      )}
    </div>
  )
}
